// ClipRadar site footer v2 — link columns + live Discord card + legal bar.
// Used on the landing page and every sub-page (via PageShell).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { CRWLogo } from '../components/primitives';

export const DISCORD_INVITE = 'https://discord.gg/7aAtq7a2e9';
const DISCORD_WIDGET = 'https://discord.com/api/guilds/1514393317323051169/widget.json';
export const SUPPORT_EMAIL = 'hello@getclipradar.com';

type FootLink =
  | { label: string; hash: string }      // section on the landing page
  | { label: string; to: string }        // internal route
  | { label: string; href: string }      // external
  | { label: string; soon: true };       // visible but inactive

const COLUMNS: { head: string; links: FootLink[] }[] = [
  {
    head: 'Product',
    links: [
      { label: 'Visual search', hash: '#visual' },
      { label: 'Speech search', hash: '#speech' },
      { label: 'Face recognition', hash: '#faces' },
      { label: 'How it works', hash: '#how' },
      { label: 'Premiere Pro panel', hash: '#premiere' },
      { label: 'Pricing', hash: '#pricing' },
    ],
  },
  {
    head: 'Use cases',
    links: [
      { label: 'Documentary filmmakers', soon: true },
      { label: 'Reality TV & unscripted', soon: true },
      { label: 'Corporate & commercial', soon: true },
      { label: 'Content creators', soon: true },
      { label: 'Wedding videographers', soon: true },
      { label: 'Houses of worship', soon: true },
    ],
  },
  {
    head: 'Resources',
    links: [
      { label: 'Download', to: '/download' },
      { label: 'Comparisons', to: '/compare' },
      { label: 'Blog', to: '/blog' },
      { label: 'Manage license', to: '/account' },
      { label: 'User guide & docs', to: '/docs' },
      { label: 'FAQ', hash: '#faq' },
      { label: 'Request a feature', href: DISCORD_INVITE },
    ],
  },
  {
    head: 'Legal',
    links: [
      { label: 'EULA', to: '/legal/eula' },
      { label: 'Privacy policy', to: '/legal/privacy' },
      { label: 'Refund policy', to: '/legal/refund' },
    ],
  },
  {
    head: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Contact', to: '/contact' },
      { label: 'Become an affiliate', to: '/affiliates' },
    ],
  },
];

function FooterLink({ link }: { link: FootLink }) {
  const [hov, setHov] = useState(false);
  const base = {
    fontSize: 13,
    lineHeight: '17px',
    textDecoration: 'none',
    color: hov ? 'var(--fg-1)' : 'var(--fg-2)',
    transition: 'color 140ms var(--ease-out)',
    cursor: 'pointer',
  } as const;
  const on = { onMouseEnter: () => setHov(true), onMouseLeave: () => setHov(false) };
  if ('soon' in link) {
    return (
      <span title="Coming soon" style={{ fontSize: 13, lineHeight: '17px', color: 'var(--fg-4)', cursor: 'default' }}>
        {link.label}
      </span>
    );
  }
  if ('to' in link) return <Link to={link.to} className="crw-footer-link" style={base} {...on}>{link.label}</Link>;
  if ('hash' in link) return <Link to={{ pathname: '/', hash: link.hash }} className="crw-footer-link" style={base} {...on}>{link.label}</Link>;
  return <a href={link.href} target="_blank" rel="noopener noreferrer" className="crw-footer-link" style={base} {...on}>{link.label}</a>;
}

function DiscordCard() {
  const [online, setOnline] = useState<number | null>(null);
  const [hov, setHov] = useState(false);
  useEffect(() => {
    fetch(DISCORD_WIDGET)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && typeof d.presence_count === 'number') setOnline(d.presence_count); })
      .catch(() => {});
  }, []);
  return (
    <div className="crw-footer-discord" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 18, borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 10, background: 'var(--signal-dim)', border: '1px solid rgba(var(--light-soft-rgb),0.3)', color: 'var(--signal-400)' }}>
          <CRWIcon d="message" size={16} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' }}>ClipRadar Discord</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.05em', color: 'var(--fg-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
            {online !== null ? (
              <>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--positive)', boxShadow: '0 0 6px rgba(60,230,160,0.7)' }}></span>
                {online} ONLINE
              </>
            ) : 'COMMUNITY'}
          </span>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-3)' }}>
        Early testers, feature votes, support — and the fastest way to reach us.
      </p>
      {/* quiet tinted button — the old solid glacier fill was the page's one
          loud button (founder, 2026-07-17); the lit tile above carries the accent */}
      <a
        href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer"
        className="crw-footer-link crw-discord-cta"
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 12px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, textDecoration: 'none', color: 'var(--signal-300)',
          background: hov ? 'rgba(var(--light-mid-rgb),0.16)' : 'var(--signal-dim)',
          boxShadow: hov ? '0 0 0 1px rgba(var(--light-soft-rgb),0.45)' : '0 0 0 1px rgba(var(--light-soft-rgb),0.28)',
          transition: 'background-color 160ms var(--ease-out), box-shadow 160ms var(--ease-out)',
        }}>
        Join the community
      </a>
    </div>
  );
}

export function BigFooter() {
  return (
    <footer style={{ borderTop: '1px solid var(--line-1)', padding: '54px 24px 30px', position: 'relative', overflow: 'hidden' }}>
      {/* faint blueprint grid, fading upward */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, transparent, black 60%)', WebkitMaskImage: 'linear-gradient(180deg, transparent, black 60%)', pointerEvents: 'none' }}></div>
      {/* the ghost wordmark — the room's name, barely lit */}
      {/* 15vw: ClipRadar is two glyphs longer than the old wordmark — 20vw overflowed */}
      {/* -0.08em (was -0.26em): raised so most of the glyphs read instead of a sliver (founder, 2026-07-17) */}
      <div aria-hidden="true" className="crw-foot-ghost" style={{ position: 'absolute', left: '50%', bottom: '-0.08em', transform: 'translateX(-50%)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '15vw', lineHeight: 1, letterSpacing: '0.005em', color: 'rgba(244,245,247,0.027)', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none' }}>ClipRadar</div>
      <div style={{ position: 'relative', maxWidth: 1120, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 36 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <Link to="/" className="crw-logo-link" style={{ textDecoration: 'none' }}><CRWLogo size={22} /></Link>
          {/* on mobile the spacer collapses so the mail button hugs the logo —
              the right edge belongs to the vertical ghost wordmark there */}
          <span className="crw-footrow-spacer" style={{ flex: 1 }}></span>
          <a href={`mailto:${SUPPORT_EMAIL}`} title={SUPPORT_EMAIL} className="crw-footer-iconbtn"
            style={{ display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 9, border: '1px solid var(--line-2)', color: 'var(--fg-2)', textDecoration: 'none', background: 'var(--surface-card)' }}>
            <CRWIcon d="at" size={15} />
          </a>
        </div>
        <div className="crw-footer-grid">
          {COLUMNS.map((c) => (
            <div key={c.head} className={`crw-footcol crw-footcol-${c.head.split(' ')[0].toLowerCase()}`} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <span className="cr-label" style={{ marginBottom: 3 }}>{c.head}</span>
              {c.links.map((l) => <FooterLink key={l.label} link={l} />)}
            </div>
          ))}
          <DiscordCard />
        </div>
        <div style={{ borderTop: '1px solid var(--line-1)', paddingTop: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-4)', letterSpacing: '0.05em' }}>
            © 2026 NDX Group. All rights reserved.
          </span>
          <span style={{ flex: 1 }}></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-4)', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ padding: '2px 7px', borderRadius: 5, border: '1px solid rgba(var(--light-soft-rgb),0.35)', color: 'var(--signal-400)' }}>BETA</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              MADE IN THE EU
              {/* inline SVG flag — the 🇪🇺 emoji renders as the letters "EU" on Windows */}
              <svg width="18" height="12" viewBox="0 0 24 16" aria-hidden="true" style={{ display: 'block', borderRadius: 2, flex: 'none' }}>
                <rect width="24" height="16" rx="2" fill="#003399" />
                {Array.from({ length: 12 }).map((_, i) => {
                  const a = (i * Math.PI) / 6;
                  return <circle key={i} cx={(12 + 5 * Math.sin(a)).toFixed(2)} cy={(8 - 5 * Math.cos(a)).toFixed(2)} r="1" fill="#FFCC00" />;
                })}
              </svg>
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
}
